/**
 * PHARMAKON — pricing engine · the remise of a Vente libre (cross-module X §9, XWP-07, 02_DESIGN §4.2).
 *
 * One remise per sale, typed either as a rate (POURCENTAGE, hundredths of a percent) or as a fixed
 * amount (MONTANT, centimes) — never both. All maths in integer centimes:
 *
 *   brut   = Σ PPA × quantité
 *   %      → remise = round_half_up(brut × pct / 100)
 *   DA     → remise = montant
 *   total  = brut − remise            (the fidélité redemption is taken later, at validation)
 *
 * Guards (XD-20): 0 ≤ pct ≤ plafond ; 0 ≤ montant ≤ brut × plafond / 100 and montant ≤ brut. The
 * ceiling is an INPUT (`plafond_pct_centiemes`, the server CONFIG `REMISE_MAX_AUTORISEE` read from the
 * POS référentiel), never a literal of the engine. Used by the server (vente.repository totals) and by
 * the SPA (live « Remise : 500,00 DA » preview, XWP-19) — the same function on both sides.
 */
import { appliquerPct, enEntierEchelle, multiplier, somme, versCentimes, versCentiemesPct } from "./money.js";

/** Mirror of Prisma `ModeRemise` (x06) — pinned by apps/server/src/__tests__/pricing.parity.test.js. */
export const MODE_REMISE_VENTE = Object.freeze({ POURCENTAGE: "POURCENTAGE", MONTANT: "MONTANT" });

/** Why a remise is refused (the server maps each code to its French message and HTTP status). */
export const ERREUR_REMISE_VENTE = Object.freeze({
  MODES_EXCLUSIFS: "MODES_EXCLUSIFS", // a rate AND an amount in the same request
  POURCENTAGE_HORS_BORNES: "POURCENTAGE_HORS_BORNES", // pct < 0 or > plafond
  MONTANT_NEGATIF: "MONTANT_NEGATIF",
  MONTANT_SUPERIEUR_BRUT: "MONTANT_SUPERIEUR_BRUT", // montant > brut
  MONTANT_HORS_PLAFOND: "MONTANT_HORS_PLAFOND", // montant > brut × plafond / 100 (XD-20)
});

const estRenseigne = (v) => v !== undefined && v !== null && v !== "";

/**
 * Reads the two wire fields of a sale into one remise. A field sent as 0 (or empty) is « not
 * typed »; a rate and an amount both above 0 are refused (modes are exclusive).
 *
 * @param {{ pct?: number|string|null, montant?: number|string|null }} saisie
 * @returns {{ mode: string|null, pct_centiemes: number, montant_centimes: number, erreur: string|null }}
 */
export function lireRemiseVente({ pct, montant } = {}) {
  const pctCentiemes = estRenseigne(pct) ? versCentiemesPct(pct) : 0;
  const montantCentimes = estRenseigne(montant) ? versCentimes(montant) : 0;
  if (pctCentiemes !== 0 && montantCentimes !== 0) {
    return { mode: null, pct_centiemes: 0, montant_centimes: 0, erreur: ERREUR_REMISE_VENTE.MODES_EXCLUSIFS };
  }
  if (montantCentimes !== 0) {
    return { mode: MODE_REMISE_VENTE.MONTANT, pct_centiemes: 0, montant_centimes: montantCentimes, erreur: null };
  }
  if (pctCentiemes !== 0) {
    return { mode: MODE_REMISE_VENTE.POURCENTAGE, pct_centiemes: pctCentiemes, montant_centimes: 0, erreur: null };
  }
  return { mode: null, pct_centiemes: 0, montant_centimes: 0, erreur: null };
}

/**
 * The largest fixed remise allowed on `brut` (centimes, rounded down — a fixed amount at the
 * ceiling is reachable, one centime above is not).
 */
export function plafondRemiseMontant(brutCentimes, plafondPctCentiemes) {
  const plafond = Math.floor(multiplier(brutCentimes, plafondPctCentiemes) / 10000);
  return Math.min(plafond, brutCentimes);
}

/**
 * The DZD effect of a remise on a basket of `brut` centimes.
 *
 * @param {{ brut_centimes: number, mode: string|null, pct_centiemes?: number, montant_centimes?: number,
 *           plafond_pct_centiemes: number }} p
 * @returns {{ remise_centimes: number, plafond_montant_centimes: number, erreur: string|null }}
 */
export function calculerRemiseVente({ brut_centimes, mode, pct_centiemes = 0, montant_centimes = 0, plafond_pct_centiemes }) {
  const plafondMontant = plafondRemiseMontant(brut_centimes, plafond_pct_centiemes);
  const refus = (erreur) => ({ remise_centimes: 0, plafond_montant_centimes: plafondMontant, erreur });
  if (mode === MODE_REMISE_VENTE.POURCENTAGE) {
    if (pct_centiemes < 0 || pct_centiemes > plafond_pct_centiemes) return refus(ERREUR_REMISE_VENTE.POURCENTAGE_HORS_BORNES);
    return { remise_centimes: appliquerPct(brut_centimes, pct_centiemes), plafond_montant_centimes: plafondMontant, erreur: null };
  }
  if (mode === MODE_REMISE_VENTE.MONTANT) {
    if (montant_centimes < 0) return refus(ERREUR_REMISE_VENTE.MONTANT_NEGATIF);
    if (montant_centimes > brut_centimes) return refus(ERREUR_REMISE_VENTE.MONTANT_SUPERIEUR_BRUT);
    if (montant_centimes > plafondMontant) return refus(ERREUR_REMISE_VENTE.MONTANT_HORS_PLAFOND);
    return { remise_centimes: montant_centimes, plafond_montant_centimes: plafondMontant, erreur: null };
  }
  return { remise_centimes: 0, plafond_montant_centimes: plafondMontant, erreur: null };
}

/**
 * The totals of a sale: brut from its lines, the remise, the total before fidélité.
 *
 * @param {Array<{ prix_unitaire_ppa: number|string|object, quantite: number }>} lignes
 * @param {{ mode: string|null, pct_centiemes?: number, montant_centimes?: number }} remise
 * @param {number} plafondPctCentiemes
 * @returns {{ brut_centimes: number, remise_centimes: number, total_centimes: number,
 *             plafond_montant_centimes: number, erreur: string|null }}
 */
export function totauxVente(lignes, remise, plafondPctCentiemes) {
  const brut = somme((lignes ?? []).map((l) => multiplier(versCentimes(l.prix_unitaire_ppa), enEntierEchelle(l.quantite, 0))));
  const r = calculerRemiseVente({ brut_centimes: brut, ...(remise ?? { mode: null }), plafond_pct_centiemes: plafondPctCentiemes });
  return {
    brut_centimes: brut,
    remise_centimes: r.remise_centimes,
    total_centimes: brut - r.remise_centimes,
    plafond_montant_centimes: r.plafond_montant_centimes,
    erreur: r.erreur,
  };
}
